/**
 * Tournament Schedule Strategy
 * - Group stage: players/teams split into groups, round-robin within each group
 * - Finals stage: top 1 per group advance to round-robin for top 2 positions
 * - Sequential match numbers (1, 2, 3, ...)
 * - Randomized schedule: no back-to-back matches for any participant
 */

import { toUtcMs } from './dateTimeUtils';

function toDate(val) {
  const ms = toUtcMs(val);
  return ms != null ? new Date(ms) : null;
}

/**
 * Fisher-Yates shuffle
 */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Split entities (players or teams) into roughly equal groups.
 * options: number (legacy preferredNumGroups) or { groupSize?, preferredNumGroups? }.
 * If groupSize is set, numGroups = ceil(n / groupSize). Otherwise use preferredNumGroups with fallbacks.
 */
export function splitIntoGroups(entities, options = 4) {
  const n = entities.length;
  if (n <= 2) return [entities];

  const opts = typeof options === 'number' ? { preferredNumGroups: options } : options;
  let numGroups;

  if (opts.groupSize != null && opts.groupSize > 0) {
    numGroups = Math.max(1, Math.ceil(n / opts.groupSize));
  } else {
    const preferred = opts.preferredNumGroups ?? 4;
    if (n < 8) numGroups = 2;
    else if (n < 12) numGroups = Math.min(4, Math.max(2, Math.floor(n / 3)));
    else numGroups = preferred;
  }

  const shuffled = shuffle(entities);
  const groups = Array.from({ length: numGroups }, () => []);
  shuffled.forEach((e, i) => groups[i % numGroups].push(e));
  return groups;
}

/**
 * Generate all round-robin matches within a single group (each pair plays once).
 * Returns matches as { left, right, groupId } where left/right are entities.
 */
function roundRobinWithinGroup(group, groupId) {
  const matches = [];
  const arr = group;
  for (let i = 0; i < arr.length; i++) {
    for (let j = i + 1; j < arr.length; j++) {
      matches.push({ left: arr[i], right: arr[j], groupId });
    }
  }
  return matches;
}

/**
 * Get participant IDs for a match (for schedule constraints).
 * Singles: [player1Id, player2Id]
 * Doubles: [...team1Players, ...team2Players]
 */
function getParticipantIds(match, matchType) {
  const t = matchType || match.matchType;
  if (t === 'singles') {
    return [match.left.id, match.right.id];
  }
  return [...(match.left.players || []), ...(match.right.players || [])];
}

/**
 * Parse "HH:MM" to minutes since midnight.
 */
function parseTime(s) {
  if (!s || typeof s !== 'string') return 18 * 60;
  const parts = s.trim().split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
}

/**
 * Format minutes since midnight to "HH:MM".
 */
function formatTime(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Generate slots (date, time, slotIndex) from config.
 * Uses a schedule window (start–end): when matches can be scheduled.
 * One table only: slots are sequential (every matchDuration minutes) within the window.
 * Produces more slots than needed so we can skip back-to-backs.
 */
export function generateSlots(config, minSlots) {
  const weekdays = config.weekdays || [1, 3, 5];
  const duration = Math.max(15, config.matchDuration || 30);

  let scheduleStart = config.scheduleStart ?? '18:00';
  let scheduleEnd = config.scheduleEnd ?? '21:00';
  if (config.timeSlots && config.timeSlots.length > 0) {
    scheduleStart = config.timeSlots[0];
    const lastMins = parseTime(config.timeSlots[config.timeSlots.length - 1]);
    scheduleEnd = formatTime(lastMins + duration);
  }

  const startMins = parseTime(scheduleStart);
  const endMins = parseTime(scheduleEnd);
  const span = endMins > startMins ? endMins - startMins : 24 * 60 - startMins + endMins;
  const slotsPerDay = Math.max(1, Math.floor(span / duration));

  const slotsPerWeek = weekdays.length * slotsPerDay;
  const weeks = Math.max(2, Math.ceil((minSlots * 1.5) / slotsPerWeek));

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  // Use this week's Monday as reference so slots can start from today when allocation happens today
  const daysSinceMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysSinceMonday);
  startDate.setHours(0, 0, 0, 0);

  const slots = [];
  let slotIndex = 0;
  // Buffer slot: one extra slot (match duration) at end of each day so rescheduled matches can be allocated
  const bufferSlotTimeMins = startMins + slotsPerDay * duration;
  for (let w = 0; w < weeks; w++) {
    for (let wd = 0; wd < weekdays.length; wd++) {
      const weekday = weekdays[wd];
      const baseDate = new Date(startDate);
      baseDate.setDate(startDate.getDate() + w * 7 + (weekday - 1));
      for (let i = 0; i < slotsPerDay; i++) {
        const timeMins = startMins + i * duration;
        const slotDate = new Date(baseDate);
        slots.push({
          slotIndex,
          date: slotDate,
          time: formatTime(timeMins),
          weekday,
        });
        slotIndex++;
      }
      const bufferSlotDate = new Date(baseDate);
      slots.push({
        slotIndex,
        date: bufferSlotDate,
        time: formatTime(bufferSlotTimeMins),
        weekday,
      });
      slotIndex++;
    }
  }
  // Include only slots on or after today so matches can be allocated from today
  const filtered = slots.filter((s) => {
    const slotDay = new Date(s.date.getFullYear(), s.date.getMonth(), s.date.getDate());
    return slotDay >= startOfToday;
  });
  return filtered;
}

/** Extended window for auto-reschedule: 1hr earlier + 30min later than normal slots. */
const RESCHEDULE_EXTEND_START_MINS = 60;
const RESCHEDULE_EXTEND_END_MINS = 30;

/**
 * Generate slots for auto-reschedule with extended window:
 * - 1 hour earlier than normal schedule start
 * - 30 mins later than normal schedule end (last slot + 30min)
 */
export function generateRescheduleSlots(config, minSlots) {
  const weekdays = config.weekdays || [1, 3, 5];
  const duration = Math.max(15, config.matchDuration || 30);

  let scheduleStart = config.scheduleStart ?? '18:00';
  let scheduleEnd = config.scheduleEnd ?? '21:00';
  if (config.timeSlots && config.timeSlots.length > 0) {
    scheduleStart = config.timeSlots[0];
    const lastMins = parseTime(config.timeSlots[config.timeSlots.length - 1]);
    scheduleEnd = formatTime(lastMins + duration);
  }

  const configStartMins = parseTime(scheduleStart);
  const configEndMins = parseTime(scheduleEnd);
  const configSpan = configEndMins > configStartMins ? configEndMins - configStartMins : 24 * 60 - configStartMins + configEndMins;
  const configSlotsPerDay = Math.max(1, Math.floor(configSpan / duration));

  const startMins = Math.max(0, configStartMins - RESCHEDULE_EXTEND_START_MINS);
  const extendedEndMins = configStartMins + configSlotsPerDay * duration + RESCHEDULE_EXTEND_END_MINS;
  const extendedSpan = extendedEndMins - startMins;
  const slotsPerDay = Math.max(1, Math.floor(extendedSpan / duration));

  const slotsPerWeek = weekdays.length * slotsPerDay;
  const weeks = Math.max(2, Math.ceil((minSlots * 1.5) / slotsPerWeek));

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysSinceMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysSinceMonday);
  startDate.setHours(0, 0, 0, 0);

  const slots = [];
  let slotIndex = 0;
  for (let w = 0; w < weeks; w++) {
    for (let wd = 0; wd < weekdays.length; wd++) {
      const weekday = weekdays[wd];
      const baseDate = new Date(startDate);
      baseDate.setDate(startDate.getDate() + w * 7 + (weekday - 1));
      for (let i = 0; i < slotsPerDay; i++) {
        const timeMins = startMins + i * duration;
        const slotDate = new Date(baseDate);
        slots.push({ slotIndex, date: slotDate, time: formatTime(timeMins), weekday });
        slotIndex++;
      }
    }
  }
  const filtered = slots.filter((s) => {
    const slotDay = new Date(s.date.getFullYear(), s.date.getMonth(), s.date.getDate());
    return slotDay >= startOfToday;
  });
  return filtered;
}

/**
 * Generate slots for auto-reschedule using only the actual tournament schedule (no buffer).
 * Same weekdays, scheduleStart–scheduleEnd, matchDuration. No extended window.
 * Use when "use buffer" is off.
 */
export function generateRescheduleSlotsStrict(config, minSlots) {
  const weekdays = config.weekdays || [1, 3, 5];
  const duration = Math.max(15, config.matchDuration || 30);

  let scheduleStart = config.scheduleStart ?? '18:00';
  let scheduleEnd = config.scheduleEnd ?? '21:00';
  if (config.timeSlots && config.timeSlots.length > 0) {
    scheduleStart = config.timeSlots[0];
    const lastMins = parseTime(config.timeSlots[config.timeSlots.length - 1]);
    scheduleEnd = formatTime(lastMins + duration);
  }

  const startMins = parseTime(scheduleStart);
  const configEndMins = parseTime(scheduleEnd);
  const configSpan = configEndMins > startMins ? configEndMins - startMins : 24 * 60 - startMins + configEndMins;
  const slotsPerDay = Math.max(1, Math.floor(configSpan / duration));

  const slotsPerWeek = weekdays.length * slotsPerDay;
  const weeks = Math.max(2, Math.ceil((minSlots * 1.5) / slotsPerWeek));

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysSinceMonday = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysSinceMonday);
  startDate.setHours(0, 0, 0, 0);

  const slots = [];
  let slotIndex = 0;
  for (let w = 0; w < weeks; w++) {
    for (let wd = 0; wd < weekdays.length; wd++) {
      const weekday = weekdays[wd];
      const baseDate = new Date(startDate);
      baseDate.setDate(startDate.getDate() + w * 7 + (weekday - 1));
      for (let i = 0; i < slotsPerDay; i++) {
        const timeMins = startMins + i * duration;
        const slotDate = new Date(baseDate);
        slots.push({ slotIndex, date: slotDate, time: formatTime(timeMins), weekday });
        slotIndex++;
      }
    }
  }
  const filtered = slots.filter((s) => {
    const slotDay = new Date(s.date.getFullYear(), s.date.getMonth(), s.date.getDate());
    return slotDay >= startOfToday;
  });
  return filtered;
}

/**
 * Get next available slot for rescheduling (uses extended window: 1hr earlier, 30min later).
 */
export function getNextAvailableSlotForReschedule(config, existingMatches) {
  const taken = new Set();
  (existingMatches || []).forEach((m) => {
    const key = toDateKey(m.scheduledDate) + '|' + (m.scheduledTime || '');
    if (key) taken.add(key);
  });
  const minSlots = (existingMatches?.length || 0) + 20;
  const allSlots = generateRescheduleSlots(config, minSlots);
  for (const slot of allSlots) {
    const key = `${slot.date.getFullYear()}-${String(slot.date.getMonth() + 1).padStart(2, '0')}-${String(slot.date.getDate()).padStart(2, '0')}|${slot.time}`;
    if (!taken.has(key)) {
      return { date: new Date(slot.date), time: slot.time };
    }
  }
  return null;
}

/**
 * Compare slot (date + time string) to a deadline (beforeDate + beforeTime).
 * Returns true if slot is strictly before the deadline.
 */
function isSlotBefore(slotDate, slotTime, beforeDate, beforeTime) {
  const before = toDate(beforeDate);
  if (!before) return false;
  const beforeDay = new Date(before.getFullYear(), before.getMonth(), before.getDate()).getTime();
  const slotDay = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate()).getTime();
  if (slotDay < beforeDay) return true;
  if (slotDay > beforeDay) return false;
  const slotMins = parseTime(slotTime || '00:00');
  const beforeMins = parseTime(beforeTime || '00:00');
  return slotMins < beforeMins;
}

/**
 * Get the earliest available slot that is (1) on or after today, (2) strictly before the given scheduled date/time.
 * Used to move future matches to an earlier slot when one is free.
 */
export function getEarliestAvailableSlotBefore(config, existingMatches, beforeScheduledDate, beforeScheduledTime) {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const taken = new Set();
  (existingMatches || []).forEach((m) => {
    const key = toDateKey(m.scheduledDate) + '|' + (m.scheduledTime || '');
    if (key) taken.add(key);
  });
  const minSlots = (existingMatches?.length || 0) + 20;
  const allSlots = generateRescheduleSlots(config, minSlots);

  for (const slot of allSlots) {
    const slotDay = new Date(slot.date.getFullYear(), slot.date.getMonth(), slot.date.getDate());
    if (slotDay < startOfToday) continue;
    if (!isSlotBefore(slot.date, slot.time, beforeScheduledDate, beforeScheduledTime)) continue;
    const key = `${slot.date.getFullYear()}-${String(slot.date.getMonth() + 1).padStart(2, '0')}-${String(slot.date.getDate()).padStart(2, '0')}|${slot.time}`;
    if (!taken.has(key)) {
      return { date: new Date(slot.date), time: slot.time };
    }
  }
  return null;
}

/**
 * Check if a match is overdue (scheduled date has passed, match not completed).
 */
export function isMatchOverdue(match, today = new Date()) {
  if (match.status === 'completed') return false;
  const schedDate = toDate(match.scheduledDate);
  if (!schedDate) return false;
  const schedDay = new Date(schedDate.getFullYear(), schedDate.getMonth(), schedDate.getDate());
  const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return schedDay < todayDay;
}

/**
 * Parse "HH:MM" to minutes since midnight (exported for reschedule).
 */
export function parseTimeToMins(s) {
  if (!s || typeof s !== 'string') return 0;
  const parts = s.trim().split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
}

/**
 * Get the last match date and time in a tournament (for reschedule upper bound).
 * @param {Array<{ scheduledDate, scheduledTime, id }>} matches - Tournament matches with scheduledDate
 * @returns {{ date: Date, time: string } | null}
 */
export function getTournamentLastMatchSlot(matches) {
  const withDate = (matches || []).filter((m) => m.scheduledDate);
  if (withDate.length === 0) return null;
  let last = null;
  let lastMins = -1;
  for (const m of withDate) {
    const d = toDate(m.scheduledDate);
    if (!d) continue;
    const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const timeMins = parseTimeToMins(m.scheduledTime || '00:00');
    const totalMins = dayStart / 60000 + timeMins;
    if (totalMins > lastMins) {
      lastMins = totalMins;
      last = { date: d, time: (m.scheduledTime || '23:59').trim() || '23:59' };
    }
  }
  return last;
}

/**
 * Check if a slot (date, time) is taken by another match.
 */
export function isSlotTaken(date, timeStr, existingMatches, excludeMatchId) {
  const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}|${(timeStr || '').trim()}`;
  for (const m of existingMatches || []) {
    if (m.id === excludeMatchId) continue;
    const d = toDate(m.scheduledDate);
    if (!d) continue;
    const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}|${(m.scheduledTime || '').trim()}`;
    if (mKey === key) return true;
  }
  return false;
}

/**
 * Normalize match scheduled date to calendar day string (YYYY-MM-DD).
 */
function toDateKey(dateOrTimestamp) {
  const d = toDate(dateOrTimestamp);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Get the next available slot on the schedule for a rescheduled match.
 * @param {object} config - Schedule config (scheduleStart, scheduleEnd, weekdays, matchDuration, etc.)
 * @param {Array<{ scheduledDate, scheduledTime }>} existingMatches - Matches already using slots (same tournament)
 * @returns {{ date: Date, time: string } | null} First slot not taken by any existing match
 */
export function getNextAvailableSlot(config, existingMatches) {
  const taken = new Set();
  (existingMatches || []).forEach((m) => {
    const key = toDateKey(m.scheduledDate) + '|' + (m.scheduledTime || '');
    if (key) taken.add(key);
  });
  const minSlots = (existingMatches?.length || 0) + 20;
  const allSlots = generateSlots(config, minSlots);
  for (const slot of allSlots) {
    const key = `${slot.date.getFullYear()}-${String(slot.date.getMonth() + 1).padStart(2, '0')}-${String(slot.date.getDate()).padStart(2, '0')}|${slot.time}`;
    if (!taken.has(key)) {
      return { date: new Date(slot.date), time: slot.time };
    }
  }
  return null;
}

/**
 * Migrate legacy scheduleConfig (timeSlots) to scheduleStart/scheduleEnd.
 * Use when loading config for display.
 */
export function migrateScheduleConfig(config) {
  if (!config) return { scheduleStart: '18:00', scheduleEnd: '21:00', matchDuration: 30, weekdays: [1, 3, 5], bufferDays: 2, singlesGroupSize: 4, doublesGroupSize: 4 };
  const c = { ...config };
  const duration = Math.max(15, c.matchDuration || 30);
  if (c.timeSlots?.length && !c.scheduleStart) {
    c.scheduleStart = c.timeSlots[0];
    const lastMins = parseTime(c.timeSlots[c.timeSlots.length - 1]);
    c.scheduleEnd = formatTime(lastMins + duration);
  }
  if (!c.scheduleStart) c.scheduleStart = '18:00';
  if (!c.scheduleEnd) c.scheduleEnd = '21:00';
  if (c.singlesGroupSize == null) c.singlesGroupSize = 4;
  if (c.doublesGroupSize == null) c.doublesGroupSize = 4;
  return c;
}

/**
 * Assign matches to slots so no participant has back-to-back matches.
 * Matches and slots are passed in; each match has .participantIds and .raw.
 * @param {Function} [availabilityChecker] - Optional. (slot, participantIds) => boolean. When false, slot is skipped for that match.
 */
export function assignSlotsNoBackToBack(matches, slots, matchType, availabilityChecker) {
  for (const m of matches) {
    const mt = matchType ?? m.raw.matchType;
    m.participantIds = getParticipantIds(m.raw, mt);
    m.matchType = mt;
  }

  const used = new Set();
  const lastSlotByParticipant = new Map();

  const assigned = [];
  const shuffledMatches = shuffle(matches);

  for (const m of shuffledMatches) {
    let placed = false;
    for (let i = 0; i < slots.length; i++) {
      if (used.has(i)) continue;
      const slot = slots[i];
      if (typeof availabilityChecker === 'function' && !availabilityChecker(slot, m.participantIds)) continue;
      const prev = i - 1;
      const next = i + 1;
      let ok = true;
      for (const pid of m.participantIds) {
        const last = lastSlotByParticipant.get(pid);
        if (last !== undefined) {
          if (last === prev || last === next) {
            ok = false;
            break;
          }
        }
      }
      if (!ok) continue;

      used.add(i);
      for (const pid of m.participantIds) {
        lastSlotByParticipant.set(pid, i);
      }
      assigned.push({ match: m.raw, slot: slots[i], matchType: m.matchType });
      placed = true;
      break;
    }
    if (!placed) {
      const first = slots.findIndex((_, idx) => {
        if (used.has(idx)) return false;
        if (typeof availabilityChecker === 'function' && !availabilityChecker(slots[idx], m.participantIds)) return false;
        return true;
      });
      if (first >= 0) {
        used.add(first);
        for (const pid of m.participantIds) {
          lastSlotByParticipant.set(pid, first);
        }
        assigned.push({ match: m.raw, slot: slots[first], matchType: m.matchType });
      } else {
        assigned.push({ match: m.raw, slot: null, matchType: m.matchType, unschedulable: true });
      }
    }
  }

  return assigned;
}

const GROUP_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Split new entities into full groups (of size groupSize) plus up to 2 "leftover".
 * If remainder is 1 or 2, they are returned as leftover to be absorbed into existing groups.
 * Returns { fullGroups: entity[][], leftover: entity[] }.
 */
function splitNewIntoGroupsAndLeftover(entities, groupSize) {
  const n = entities.length;
  if (n === 0) return { fullGroups: [], leftover: [] };
  if (n <= 2) return { fullGroups: [], leftover: [...entities] };
  const gs = Math.max(2, groupSize || 4);
  const fullCount = Math.floor(n / gs);
  const remainder = n - fullCount * gs;
  const shuffled = shuffle([...entities]);
  if (remainder >= 3 || fullCount === 0) {
    const groups = splitIntoGroups(entities, { groupSize: gs });
    return { fullGroups: groups, leftover: [] };
  }
  const leftover = remainder >= 1 ? shuffled.slice(-remainder) : [];
  const used = fullCount * gs;
  const forFull = used > 0 ? shuffled.slice(0, used) : [];
  const fullGroups = [];
  for (let i = 0; i < fullCount; i++) {
    fullGroups.push(forFull.slice(i * gs, (i + 1) * gs));
  }
  return { fullGroups, leftover };
}

/**
 * Absorb 1 or 2 leftover entities into existing groups. No group exceeds groupSize * 1.5.
 * existingGroups: [ { groupId, members: entity[] } ]
 * Returns new array of groups with leftovers distributed (prefer smallest groups first).
 */
function absorbLeftoverIntoExisting(leftover, existingGroups, groupSize) {
  if (leftover.length === 0 || existingGroups.length === 0) return existingGroups;
  const maxPerGroup = Math.floor((groupSize || 4) * 1.5);
  const updated = existingGroups.map(g => ({ groupId: g.groupId, members: [...g.members] }));
  for (const e of leftover) {
    const withRoom = updated.filter(g => g.members.length < maxPerGroup);
    if (withRoom.length === 0) break;
    withRoom.sort((a, b) => a.members.length - b.members.length);
    withRoom[0].members.push(e);
  }
  return updated;
}

/**
 * Build existing group membership and existing pairs from match docs.
 * matchDoc: { matchType, groupId, player1Id, player2Id, team1Id, team2Id, team1Players, team2Players }
 */
function parseExistingGroupsFromMatches(matchDocs) {
  const singles = {};
  const doubles = {};
  const singlesPairs = {}; // groupId -> Set of "id1,id2" (sorted)
  const doublesPairs = {}; // groupId -> Set of "teamId1,teamId2" (sorted)
  const teamById = {};
  for (const m of matchDocs) {
    const gid = m.groupId || 'A';
    if (m.matchType === 'singles') {
      if (!singles[gid]) singles[gid] = new Set();
      singles[gid].add(m.player1Id);
      singles[gid].add(m.player2Id);
      if (!singlesPairs[gid]) singlesPairs[gid] = new Set();
      const p = [m.player1Id, m.player2Id].sort().join(',');
      singlesPairs[gid].add(p);
    } else {
      if (!doubles[gid]) doubles[gid] = new Set();
      const t1 = m.team1Id || '';
      const t2 = m.team2Id || '';
      if (t1) {
        doubles[gid].add(t1);
        teamById[t1] = { teamId: t1, teamName: m.team1Name || t1, players: m.team1Players || [] };
      }
      if (t2) {
        doubles[gid].add(t2);
        teamById[t2] = { teamId: t2, teamName: m.team2Name || t2, players: m.team2Players || [] };
      }
      if (t1 && t2) {
        if (!doublesPairs[gid]) doublesPairs[gid] = new Set();
        doublesPairs[gid].add([t1, t2].sort().join(','));
      }
    }
  }
  return { singles, doubles, singlesPairs, doublesPairs, teamById };
}

/**
 * Generate only NEW group-stage matches for newly registered players/teams.
 * - New players/teams are split into new groups; if 1 or 2 are left out, they are absorbed into
 *   existing groups (existing groups may go up to groupSize * 1.5).
 * - existingMatchDocs: array of existing match documents (must include matchType, groupId, player/team ids).
 * - newSinglesPlayers: [ playerId, ... ]
 * - newDoublesTeams: [ { teamId, teamName, players }, ... ]
 * - startMatchNumber: next match number to use.
 * Returns array of new match docs.
 */
export function generateAdditionalGroupMatchesForNew(
  existingMatchDocs,
  newSinglesPlayers,
  newDoublesTeams,
  tournamentId,
  config,
  allPlayers,
  startMatchNumber
) {
  const rawMatches = [];
  const groupSizeSingles = (config.singlesGroupSize != null && config.singlesGroupSize >= 2)
    ? config.singlesGroupSize : 4;
  const groupSizeDoubles = (config.doublesGroupSize != null && config.doublesGroupSize >= 2)
    ? config.doublesGroupSize : 4;
  const { singles: existingSingles, doubles: existingDoubles, singlesPairs, doublesPairs, teamById } =
    parseExistingGroupsFromMatches(existingMatchDocs);
  const existingSinglesLabels = Object.keys(existingSingles).sort();
  const existingDoublesLabels = Object.keys(existingDoubles).sort();
  let nextLabelIndex = Math.max(
    GROUP_LABELS.indexOf(existingSinglesLabels[existingSinglesLabels.length - 1] || ''),
    GROUP_LABELS.indexOf(existingDoublesLabels[existingDoublesLabels.length - 1] || ''),
    -1
  ) + 1;

  if (newSinglesPlayers.length >= 2) {
    const { fullGroups, leftover } = splitNewIntoGroupsAndLeftover(newSinglesPlayers, groupSizeSingles);
    const existingAsArray = existingSinglesLabels.map(gid => ({
      groupId: gid,
      members: [...(existingSingles[gid] || [])]
    }));
    const afterAbsorb = absorbLeftoverIntoExisting(leftover, existingAsArray, groupSizeSingles);
    fullGroups.forEach((grp) => {
      const gid = GROUP_LABELS[nextLabelIndex] || 'Z';
      nextLabelIndex++;
      const rr = roundRobinWithinGroup(grp, gid);
      rr.forEach((m) => rawMatches.push({
        ...m, phase: 'group', matchType: 'singles',
        left: { id: m.left }, right: { id: m.right }
      }));
    });
    afterAbsorb.forEach(({ groupId, members }) => {
      const existingSet = existingSingles[groupId] || new Set();
      const newInGroup = members.filter(id => !existingSet.has(id));
      if (newInGroup.length === 0) return;
      const pairs = singlesPairs[groupId] || new Set();
      for (let i = 0; i < members.length; i++) {
        for (let j = i + 1; j < members.length; j++) {
          const key = [members[i], members[j]].sort().join(',');
          if (pairs.has(key)) continue;
          rawMatches.push({
            matchType: 'singles', phase: 'group', groupId,
            left: { id: members[i] }, right: { id: members[j] }
          });
        }
      }
    });
  }

  if (newDoublesTeams.length >= 2) {
    newDoublesTeams.forEach((t) => { teamById[t.teamId] = t; });
    const { fullGroups, leftover } = splitNewIntoGroupsAndLeftover(newDoublesTeams, groupSizeDoubles);
    const existingTeamsByGid = {};
    existingDoublesLabels.forEach(gid => { existingTeamsByGid[gid] = [...(existingDoubles[gid] || [])]; });
    const afterAbsorb = absorbLeftoverIntoExisting(
      leftover.map(t => t.teamId),
      existingDoublesLabels.map(gid => ({ groupId: gid, members: existingTeamsByGid[gid] || [] })),
      groupSizeDoubles
    );
    const teamsByIdMap = new Map(newDoublesTeams.map(t => [t.teamId, t]));
    fullGroups.forEach((grp) => {
      const gid = GROUP_LABELS[nextLabelIndex] || 'Z';
      nextLabelIndex++;
      const rr = roundRobinWithinGroup(grp, gid);
      rr.forEach((m) => rawMatches.push({
        ...m, phase: 'group', matchType: 'doubles',
        left: m.left, right: m.right
      }));
    });
    afterAbsorb.forEach(({ groupId, members }) => {
      const teamObjs = members.map(tid => teamById[tid] || teamsByIdMap.get(tid) || { teamId: tid, teamName: tid, players: [] });
      const existingSet = existingDoubles[groupId] || new Set();
      const newInGroup = teamObjs.filter(t => !existingSet.has(t.teamId));
      if (newInGroup.length === 0) return;
      const pairs = doublesPairs[groupId] || new Set();
      for (let i = 0; i < teamObjs.length; i++) {
        for (let j = i + 1; j < teamObjs.length; j++) {
          const key = [teamObjs[i].teamId, teamObjs[j].teamId].sort().join(',');
          if (pairs.has(key)) continue;
          rawMatches.push({
            matchType: 'doubles', phase: 'group', groupId,
            left: teamObjs[i], right: teamObjs[j]
          });
        }
      }
    });
  }

  if (rawMatches.length === 0) return [];

  const slots = generateSlots(config, rawMatches.length);
  const withMeta = rawMatches.map((r) => ({
    raw: r,
    participantIds: r.matchType === 'singles'
      ? [r.left.id, r.right.id]
      : [...(r.left.players || []), ...(r.right.players || [])],
    matchType: r.matchType
  }));
  const assigned = assignSlotsNoBackToBack(withMeta, slots, null);
  let matchNumber = startMatchNumber - 1;
  const docs = [];
  const resolver = createBalancedRefereeResolver(allPlayers);
  for (const { match: m, slot, matchType } of assigned) {
    matchNumber++;
    const scheduledDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
    const enriched = {
      ...m,
      matchNumber,
      scheduledDate,
      scheduledTime: slot.time,
      scheduledWeekday: slot.weekday,
    };
    const toDoc = matchType === 'singles' ? toSinglesMatchDoc : toDoublesMatchDoc;
    docs.push(toDoc(enriched, tournamentId, config, allPlayers, resolver));
  }
  return docs;
}

/**
 * Build group-stage matches only for singles.
 * groupSize: target players per group (optional). If set, numGroups = ceil(n / groupSize).
 */
function buildSinglesGroupMatches(players, groupSize) {
  const groups = splitIntoGroups(players, groupSize != null ? { groupSize } : undefined);
  const groupMatches = [];
  const groupIds = groups.map((_, i) => GROUP_LABELS[i]);

  groups.forEach((g, i) => {
    const gid = groupIds[i];
    const rr = roundRobinWithinGroup(g, gid);
    rr.forEach((m) => groupMatches.push({ ...m, phase: 'group' }));
  });

  return { groupMatches, groups, groupIds };
}

/**
 * Build group-stage matches only for doubles.
 * groupSize: target teams per group (optional). If set, numGroups = ceil(n / groupSize).
 */
function buildDoublesGroupMatches(teams, groupSize) {
  const groups = splitIntoGroups(teams, groupSize != null ? { groupSize } : undefined);
  const groupMatches = [];
  const groupIds = groups.map((_, i) => GROUP_LABELS[i]);

  groups.forEach((g, i) => {
    const gid = groupIds[i];
    const rr = roundRobinWithinGroup(g, gid);
    rr.forEach((m) => groupMatches.push({ ...m, phase: 'group' }));
  });

  return { groupMatches, groups, groupIds };
}

/**
 * Convert internal match format to full match doc (singles).
 */
function toSinglesMatchDoc(m, tournamentId, config, allPlayers, refereeResolver) {
  const refereeId = refereeResolver(m);
  return {
    tournamentId,
    matchType: 'singles',
    status: 'scheduled',
    bestOf: 3,
    phase: m.phase,
    groupId: m.groupId || null,
    round: m.round != null ? m.round : 1,
    matchNumber: m.matchNumber,
    player1Id: m.left.id,
    player2Id: m.right.id,
    scheduledDate: m.scheduledDate,
    scheduledTime: m.scheduledTime,
    scheduledWeekday: m.scheduledWeekday,
    refereeId,
    matchDuration: config.matchDuration || 30,
    bufferDays: config.bufferDays || 2,
    createdAt: new Date(),
  };
}

/**
 * Convert internal match format to full match doc (doubles).
 */
function toDoublesMatchDoc(m, tournamentId, config, allPlayers, refereeResolver) {
  const refereeId = refereeResolver(m);
  return {
    tournamentId,
    matchType: 'doubles',
    status: 'scheduled',
    bestOf: 3,
    phase: m.phase,
    groupId: m.groupId || null,
    round: m.round != null ? m.round : 1,
    matchNumber: m.matchNumber,
    team1Id: m.left.teamId,
    team1Name: m.left.teamName,
    team1Players: m.left.players || [],
    team2Id: m.right.teamId,
    team2Name: m.right.teamName,
    team2Players: m.right.players || [],
    scheduledDate: m.scheduledDate,
    scheduledTime: m.scheduledTime,
    scheduledWeekday: m.scheduledWeekday,
    refereeId,
    matchDuration: config.matchDuration || 30,
    bufferDays: config.bufferDays || 2,
    createdAt: new Date(),
  };
}

/**
 * Create a stateful referee resolver that balances referee assignments.
 * For each match, picks the eligible player (not participating) with the fewest
 * referee assignments so far. Ties broken by player id for determinism.
 * @param {string[]} allPlayers - All player IDs
 * @param {string} [matchType] - 'singles' | 'doubles'; if omitted, m.matchType is used (e.g. generateAllGroupMatches).
 */
function createBalancedRefereeResolver(allPlayers, matchType = null) {
  const refereeCount = new Map();

  return (m) => {
    const mt = matchType ?? m.matchType;
    let exclude = [];
    if (mt === 'singles') {
      const lid = typeof m.left === 'string' ? m.left : m.left?.id;
      const rid = typeof m.right === 'string' ? m.right : m.right?.id;
      exclude = [lid, rid].filter(Boolean);
    } else {
      exclude = [...(m.left?.players || []), ...(m.right?.players || [])];
    }
    const available = allPlayers.filter((p) => !exclude.includes(p));
    if (available.length === 0) return null;

    let best = available[0];
    let bestCount = refereeCount.get(best) ?? 0;
    for (const p of available.slice(1)) {
      const c = refereeCount.get(p) ?? 0;
      if (c < bestCount || (c === bestCount && p < best)) {
        best = p;
        bestCount = c;
      }
    }
    refereeCount.set(best, bestCount + 1);
    return best;
  };
}

/**
 * Generate group-stage singles matches: groups, round-robin within each, match numbers, randomized schedule.
 * Returns array of match documents ready for Firestore.
 */
export function generateSinglesSchedule(players, tournamentId, config, allPlayers) {
  if (!players.length || players.length < 2) return [];

  const groupSize = (config.singlesGroupSize != null && config.singlesGroupSize >= 2)
    ? config.singlesGroupSize
    : undefined;
  const { groupMatches } = buildSinglesGroupMatches(players, groupSize);
  const rawMatches = groupMatches
    .map((m) => {
      const leftId = typeof m.left === 'string' ? m.left : m.left?.id;
      const rightId = typeof m.right === 'string' ? m.right : m.right?.id;
      if (!leftId || !rightId) return null;
      return {
        ...m,
        left: { id: leftId },
        right: { id: rightId },
      };
    })
    .filter(Boolean);

  const minSlots = rawMatches.length;
  const slots = generateSlots(config, minSlots);

  const withMeta = rawMatches.map((r) => ({ raw: r }));
  const assigned = assignSlotsNoBackToBack(withMeta, slots, 'singles');

  let matchNumber = 0;
  const resolver = createBalancedRefereeResolver(allPlayers, 'singles');

  const docs = [];
  for (const { match: m, slot } of assigned) {
    matchNumber++;
    const scheduledDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
    const enriched = {
      ...m,
      matchNumber,
      scheduledDate,
      scheduledTime: slot.time,
      scheduledWeekday: slot.weekday,
    };
    docs.push(toSinglesMatchDoc(enriched, tournamentId, config, allPlayers, resolver));
  }

  return docs;
}

/**
 * Generate group-stage doubles matches: groups, round-robin within each, match numbers, randomized schedule.
 */
export function generateDoublesSchedule(teams, tournamentId, config, allPlayers) {
  if (!teams.length || teams.length < 2) return [];

  const groupSize = (config.doublesGroupSize != null && config.doublesGroupSize >= 2)
    ? config.doublesGroupSize
    : undefined;
  const { groupMatches } = buildDoublesGroupMatches(teams, groupSize);
  const rawMatches = groupMatches.map((m) => ({
    ...m,
    left: { teamId: m.left.teamId, teamName: m.left.teamName, players: m.left.players || [] },
    right: { teamId: m.right.teamId, teamName: m.right.teamName, players: m.right.players || [] },
  }));

  const minSlots = rawMatches.length;
  const slots = generateSlots(config, minSlots);

  const withMeta = rawMatches.map((r) => ({ raw: r }));
  const assigned = assignSlotsNoBackToBack(withMeta, slots, 'doubles');

  let matchNumber = 0;
  const resolver = createBalancedRefereeResolver(allPlayers, 'doubles');

  const docs = [];
  for (const { match: m, slot } of assigned) {
    matchNumber++;
    const scheduledDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
    const enriched = {
      ...m,
      matchNumber,
      scheduledDate,
      scheduledTime: slot.time,
      scheduledWeekday: slot.weekday,
    };
    docs.push(toDoublesMatchDoc(enriched, tournamentId, config, allPlayers, resolver));
  }

  return docs;
}

/**
 * Generate finals round-robin matches (top 1 per group).
 * Used after group stage is complete. startMatchNumber continues from existing group matches.
 * winnersSingles: [playerId, ...] or winnersDoubles: [{ teamId, teamName, players }, ...]
 */
export function generateFinalsSchedule(matchType, winners, tournamentId, config, allPlayers, startMatchNumber) {
  const n = winners.length;
  if (n < 2) return [];

  const rawMatches = [];
  if (matchType === 'singles') {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        rawMatches.push({
          matchType: 'singles',
          left: { id: winners[i] },
          right: { id: winners[j] },
          phase: 'finals',
          groupId: null,
          round: 2,
        });
      }
    }
  } else {
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        rawMatches.push({
          matchType: 'doubles',
          left: winners[i],
          right: winners[j],
          phase: 'finals',
          groupId: null,
          round: 2,
        });
      }
    }
  }

  const minSlots = rawMatches.length;
  const slots = generateSlots(config, minSlots);

  const withMeta = rawMatches.map((r) => ({ raw: r }));
  const assigned = assignSlotsNoBackToBack(withMeta, slots, null);

  let matchNumber = startMatchNumber;
  const resolver = createBalancedRefereeResolver(allPlayers, matchType);

  const docs = [];
  const toDoc = matchType === 'singles' ? toSinglesMatchDoc : toDoublesMatchDoc;

  for (const { match: m, slot } of assigned) {
    matchNumber++;
    const scheduledDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
    const enriched = {
      ...m,
      matchNumber,
      scheduledDate,
      scheduledTime: slot.time,
      scheduledWeekday: slot.weekday,
    };
    docs.push(toDoc(enriched, tournamentId, config, allPlayers, resolver));
  }

  return docs;
}

/**
 * Generate all group-stage matches (singles + doubles) with unified scheduling.
 * Global match numbers 1, 2, 3, ... and no back-to-back for any player across singles/doubles.
 * players: [playerId, ...], teams: [{ teamId, teamName, players }, ...]
 * options: { availabilityChecker?: (slot, participantIds) => boolean }
 * Returns array of match docs (scheduled and unschedulable).
 */
export function generateAllGroupMatches(players, teams, tournamentId, config, allPlayers, options = {}) {
  const rawMatches = [];

  if (players.length >= 2) {
    const singlesGroupSize = (config.singlesGroupSize != null && config.singlesGroupSize >= 2)
      ? config.singlesGroupSize
      : undefined;
    const { groupMatches } = buildSinglesGroupMatches(players, singlesGroupSize);
    groupMatches.forEach((m) => {
      const leftId = typeof m.left === 'string' ? m.left : m.left?.id;
      const rightId = typeof m.right === 'string' ? m.right : m.right?.id;
      if (!leftId || !rightId) return;
      rawMatches.push({
        ...m,
        matchType: 'singles',
        left: { id: leftId },
        right: { id: rightId },
      });
    });
  }

  if (teams.length >= 2) {
    const doublesGroupSize = (config.doublesGroupSize != null && config.doublesGroupSize >= 2)
      ? config.doublesGroupSize
      : undefined;
    const { groupMatches } = buildDoublesGroupMatches(teams, doublesGroupSize);
    groupMatches.forEach((m) => {
      rawMatches.push({
        ...m,
        matchType: 'doubles',
        left: { teamId: m.left.teamId, teamName: m.left.teamName, players: m.left.players || [] },
        right: { teamId: m.right.teamId, teamName: m.right.teamName, players: m.right.players || [] },
      });
    });
  }

  if (rawMatches.length === 0) return [];

  const slots = generateSlots(config, rawMatches.length);
  const withMeta = rawMatches.map((r) => ({ raw: r }));
  const availabilityChecker = options.availabilityChecker;
  const assigned = assignSlotsNoBackToBack(withMeta, slots, null, availabilityChecker);

  let matchNumber = 0;
  const docs = [];
  const resolver = createBalancedRefereeResolver(allPlayers);

  for (const { match: m, slot, matchType, unschedulable } of assigned) {
    matchNumber++;
    if (unschedulable || !slot) {
      const baseDoc = matchType === 'singles'
        ? {
            tournamentId,
            matchType: 'singles',
            phase: m.phase,
            groupId: m.groupId || null,
            round: m.round != null ? m.round : 1,
            matchNumber,
            player1Id: m.left?.id,
            player2Id: m.right?.id,
          }
        : {
            tournamentId,
            matchType: 'doubles',
            phase: m.phase,
            groupId: m.groupId || null,
            round: m.round != null ? m.round : 1,
            matchNumber,
            team1Id: m.left?.teamId,
            team1Name: m.left?.teamName,
            team1Players: m.left?.players || [],
            team2Id: m.right?.teamId,
            team2Name: m.right?.teamName,
            team2Players: m.right?.players || [],
          };
      docs.push({
        ...baseDoc,
        status: 'unschedulable',
        bestOf: 3,
        refereeId: resolver(m),
        matchDuration: config.matchDuration || 30,
        bufferDays: config.bufferDays || 2,
        createdAt: new Date(),
      });
    } else {
      const scheduledDate = slot.date instanceof Date ? new Date(slot.date) : new Date(slot.date);
      const enriched = {
        ...m,
        matchNumber,
        scheduledDate,
        scheduledTime: slot.time,
        scheduledWeekday: slot.weekday,
      };
      const toDoc = matchType === 'singles' ? toSinglesMatchDoc : toDoublesMatchDoc;
      docs.push(toDoc(enriched, tournamentId, config, allPlayers, resolver));
    }
  }

  return docs;
}
